import {
  type GetApiJingleCollectionsIdJinglesPathParams,
  type GetApiJingleCollectionsIdJinglesQueryParams,
  type JingleModel,
  type JingleModelPagedResult,
  getApiJingleCollectionsIdJingles,
} from '@/shared/api'
import { DEBOUNCE_COMMON_TIMEOUT_IN_MS } from '@/shared/config'
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@/shared/lib/query'
import { type Query, createQuery } from '@farfetched/core'
import {
  type EventCallable,
  type Store,
  type StoreWritable,
  combine,
  createEvent,
  createStore,
  sample,
} from 'effector'
import { debounce } from 'patronum'

export interface JingleCollectionsIdJinglesQueryModelBase {
  // Availability
  $isAvailable: StoreWritable<boolean>
  availableSet: EventCallable<void>
  unavailableSet: EventCallable<void>
  //   Collection Id
  $collectionId: StoreWritable<string>
  collectionIdChanged: EventCallable<string>
  collectionIdReset: EventCallable<void>

  //   Filters
  $filters: StoreWritable<GetApiJingleCollectionsIdJinglesQueryParams>
  filtersInit: EventCallable<
    Partial<GetApiJingleCollectionsIdJinglesQueryParams>
  >
  filtersChanged: EventCallable<
    Partial<GetApiJingleCollectionsIdJinglesQueryParams>
  >
  filtersReset: EventCallable<void>
  //   Params
  $params: Store<{
    params: GetApiJingleCollectionsIdJinglesQueryParams
  }>
  // Query
  query: Query<
    {
      id: GetApiJingleCollectionsIdJinglesPathParams['Id']
      params?: GetApiJingleCollectionsIdJinglesQueryParams
    },
    JingleModelPagedResult,
    unknown
  >
  refreshed: EventCallable<void>
  // Accumulated
  $accumulatedData: StoreWritable<JingleModel[]>
}

export interface JingleCollectionsIdJinglesQueryModelWithPagination
  extends JingleCollectionsIdJinglesQueryModelBase {
  $page: StoreWritable<number>
  pageChanged: EventCallable<number>
  pageReset: EventCallable<void>
  $pageSize: StoreWritable<number>
  pageSizeChanged: EventCallable<number>
}

interface JingleCollectionsIdJinglesQueryModelParams {
  initialFilters?: Partial<GetApiJingleCollectionsIdJinglesQueryParams>
  usePagination: boolean
  accumulate: boolean
}

export function createJingleCollectionsIdJinglesQueryModel(
  {
    initialFilters = {},
    usePagination = true,
    accumulate = false,
  }: JingleCollectionsIdJinglesQueryModelParams = {
    initialFilters: {},
    usePagination: true,
    accumulate: false,
  },
):
  | JingleCollectionsIdJinglesQueryModelWithPagination
  | JingleCollectionsIdJinglesQueryModelBase {
  // Availability
  const availableSet = createEvent()
  const unavailableSet = createEvent()
  const $isAvailable = createStore(false)
    .on(availableSet, () => true)
    .on(unavailableSet, () => false)

  // Collection Id
  const collectionIdChanged = createEvent<string>()
  const collectionIdReset = createEvent()
  const $collectionId = createStore<string>('')
    .on(collectionIdChanged, (_, id) => id)
    .on(collectionIdReset, () => '')

  // Filters
  const filtersInit =
    createEvent<Partial<GetApiJingleCollectionsIdJinglesQueryParams>>()
  const filtersChanged =
    createEvent<Partial<GetApiJingleCollectionsIdJinglesQueryParams>>()
  const filtersReset = createEvent()
  const $filters = createStore<GetApiJingleCollectionsIdJinglesQueryParams>(
    initialFilters,
  )
    .on(filtersInit, (_, payload) => ({ ...initialFilters, ...payload }))
    .on(filtersChanged, (state, payload) => ({ ...state, ...payload }))
    .on(filtersReset, () => initialFilters)

  // Pagination

  const $page = createStore<number>(DEFAULT_PAGE)

  const pageChanged = createEvent<number>()
  const pageReset = createEvent()
  $page
    .on(pageChanged, (_, page) => Math.max(DEFAULT_PAGE, page))
    .on(pageReset, () => DEFAULT_PAGE)

  const $pageSize = createStore<number>(DEFAULT_PAGE_SIZE)
  const pageSizeChanged = createEvent<number>()
  $pageSize.on(pageSizeChanged, (_, size) => size)

  sample({
    clock: $filters,
    target: pageReset,
  })

  // Query params
  const $params = combine(
    $collectionId,
    $filters,
    $page,
    $pageSize,
    (collectionId, filters, page, pageSize) => {
      // Filters
      const params: GetApiJingleCollectionsIdJinglesQueryParams = { ...filters }
      // Pagination
      if (usePagination) {
        params.Limit = pageSize
        params.Offset = (page - 1) * pageSize
      }
      return { id: collectionId, params }
    },
  )

  // Query
  const query = createQuery({ handler: getApiJingleCollectionsIdJingles })

  // Accumulation
  const initialAccumulatedData: JingleModel[] = []
  const accumulatedDataReset = createEvent()
  const $accumulatedData = createStore<JingleModel[]>(initialAccumulatedData)
    .on(query.finished.success, (state, { result }) => {
      if (accumulate) {
        const map = new Map(state.map((item) => [item.id, item]))
        for (const item of result.items) {
          map.set(item.id, item)
        }
        return Array.from(map.values())
      }
      return result.items
    })
    .on(accumulatedDataReset, () => initialAccumulatedData)

  // Debounce
  const paramsDebounced = debounce({
    source: $params,
    timeout: DEBOUNCE_COMMON_TIMEOUT_IN_MS,
  })

  // Start query
  sample({
    clock: paramsDebounced,
    filter: $isAvailable,
    target: query.start,
  })

  //   Refresh
  const refreshed = createEvent()
  sample({
    source: $params,
    clock: refreshed,
    filter: $isAvailable,
    target: query.start,
  })

  // Reset logic
  sample({
    clock: unavailableSet,
    target: query.reset,
  })

  sample({
    clock: query.reset,
    target: [collectionIdReset, filtersReset, pageReset],
  })

  const model: JingleCollectionsIdJinglesQueryModelBase = {
    // Availability
    $isAvailable,
    availableSet,
    unavailableSet,
    // Collection Id
    $collectionId,
    collectionIdChanged,
    collectionIdReset,
    // Filters
    $filters,
    filtersInit,
    filtersChanged,
    filtersReset,
    // Params
    $params,
    // Query
    query,
    refreshed,
    // Accumulation
    $accumulatedData,
  }

  const modelWithPagination: JingleCollectionsIdJinglesQueryModelWithPagination =
    {
      ...model,
      $page,
      pageChanged,
      pageReset,
      $pageSize,
      pageSizeChanged,
    }

  if (usePagination) {
    return modelWithPagination
  }

  return model
}
